import { colors, glassCard, glassModal, typography, cardBlur, modalBlur, inputSx, btnPrimary, radius } from '../../theme';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    collection, query, where, Timestamp,
    onSnapshot, doc, getDoc, updateDoc, arrayUnion, setDoc,
} from 'firebase/firestore';
import { db, auth } from '../../firebase';
import {
    GoogleAuthProvider, signInWithPopup, createUserWithEmailAndPassword,
    signInWithEmailAndPassword, onAuthStateChanged,
} from 'firebase/auth';
import { Box, Typography, CircularProgress, IconButton, Button, TextField } from '@mui/material';
import { ArrowBack, Star, StarBorder, CheckCircle, Google } from '@mui/icons-material';
import logo from '../../images/logo.svg';

function formatTime(minutes) {
    if (!minutes && minutes !== 0) return '—';
    const h = Math.floor(minutes / 60), m = minutes % 60;
    return `${h % 12 === 0 ? 12 : h % 12}:${m.toString().padStart(2,'0')} ${h < 12 ? 'AM' : 'PM'}`;
}

// ─── Save review ──────────────────────────────────────────────────────────────
async function saveReview({ bookingId, shopId, barberId, barberName, customerName, selectedCuts, rating, review, grandTotal, surcharge, servicesTotal }) {
    const now       = new Date().toISOString();
    const reviewObj = { bookingId, barberName, customerName: customerName ?? 'Walk-in', rating: rating > 0 ? rating : null, review: review.trim() || null, services: selectedCuts.map(c => c.label), createdAt: now };

    const bookingUpdates = {};
    if (selectedCuts.length > 0) { bookingUpdates.services = selectedCuts.map(c => c.id); bookingUpdates.serviceLabels = selectedCuts.map(c => c.label); bookingUpdates.service = selectedCuts[0].id; bookingUpdates.serviceLabel = selectedCuts[0].label; bookingUpdates.servicePrice = servicesTotal; bookingUpdates.totalPrice = grandTotal; }
    if (rating > 0)    bookingUpdates.rating = rating;
    if (review.trim()) bookingUpdates.review = review.trim();
    if (Object.keys(bookingUpdates).length > 0) await updateDoc(doc(db,'bookings',bookingId), bookingUpdates);

    if (barberId) {
        const barberRef  = doc(db,'shops',shopId,'barbers',barberId);
        const barberSnap = await getDoc(barberRef);
        if (barberSnap.exists()) {
            const bd = barberSnap.data();
            const barberUpdates = { reviews: arrayUnion(reviewObj) };
            if (rating > 0) {
                const oldCount = bd.ratingCount ?? bd.ratings?.totalReviews ?? 0;
                const oldTotal = bd.ratingTotal ?? (oldCount * (bd.ratings?.average ?? bd.rating ?? 0));
                const newCount = oldCount + 1, newTotal = oldTotal + rating;
                barberUpdates.ratingTotal = newTotal; barberUpdates.ratingCount = newCount;
                barberUpdates.rating = Math.round((newTotal / newCount) * 10) / 10;
                barberUpdates.ratings = { average: barberUpdates.rating, totalReviews: newCount };
            }
            await updateDoc(barberRef, barberUpdates);
        }
    }

    const shopRef  = doc(db,'shops',shopId);
    const shopSnap = await getDoc(shopRef);
    if (shopSnap.exists()) {
        const sd = shopSnap.data();
        const shopUpdates = { reviews: arrayUnion(reviewObj) };
        if (rating > 0) {
            const oldCount = sd.ratingCount ?? sd.ratings?.totalReviews ?? 0;
            const oldTotal = sd.ratingTotal ?? (oldCount * (sd.ratings?.average ?? sd.rating ?? 0));
            const newCount = oldCount + 1, newTotal = oldTotal + rating;
            shopUpdates.ratingTotal = newTotal; shopUpdates.ratingCount = newCount;
            shopUpdates.rating = Math.round((newTotal / newCount) * 10) / 10;
            shopUpdates.ratings = { average: shopUpdates.rating, totalReviews: newCount };
        }
        await updateDoc(shopRef, shopUpdates);
    }
}

// ─── Link booking to user ─────────────────────────────────────────────────────
async function linkBookingToUser(uid, bookingId, shopId, barberId, barberName, selectedCuts, customerPhone) {
    try {
        await updateDoc(doc(db,'bookings',bookingId), { userId: uid });
        const userRef  = doc(db,'users',uid);
        const userSnap = await getDoc(userRef);
        const visitEntry = { bookingId, shopId, barberId, barberName, services: selectedCuts.map(c => c.label), visitedAt: new Date().toISOString() };
        if (userSnap.exists()) {
            await updateDoc(userRef, { visits: arrayUnion(visitEntry), lastBarberId: barberId, lastBarberName: barberName, lastServices: selectedCuts.map(c => c.label), lastShopId: shopId, ...(customerPhone ? { phone: customerPhone } : {}) });
        } else {
            await setDoc(userRef, { uid, visits: [visitEntry], lastBarberId: barberId, lastBarberName: barberName, lastServices: selectedCuts.map(c => c.label), lastShopId: shopId, ...(customerPhone ? { phone: customerPhone } : {}), createdAt: new Date().toISOString() });
        }
    } catch (e) { console.error('linkBookingToUser error:', e); }
}

// ─── Account Step ─────────────────────────────────────────────────────────────
const AccountStep = ({ bookingId, shopId, barberId, barberName, selectedCuts, customerPhone, onDone }) => {
    const [mode,     setMode]     = useState('prompt');
    const [email,    setEmail]    = useState('');
    const [password, setPassword] = useState('');
    const [isNew,    setIsNew]    = useState(true);
    const [loading,  setLoading]  = useState(false);
    const [error,    setError]    = useState('');

    const handleGoogle = async () => {
        setLoading(true); setError('');
        try {
            const result = await signInWithPopup(auth, new GoogleAuthProvider());
            await linkBookingToUser(result.user.uid, bookingId, shopId, barberId, barberName, selectedCuts, customerPhone);
            setMode('done'); setTimeout(onDone, 1800);
        } catch (e) { setError('Google sign-in failed. Try again.'); }
        setLoading(false);
    };

    const handleEmail = async () => {
        if (!email || !password) { setError('Please fill in both fields.'); return; }
        setLoading(true); setError('');
        try {
            const r = isNew ? await createUserWithEmailAndPassword(auth, email, password) : await signInWithEmailAndPassword(auth, email, password);
            await linkBookingToUser(r.user.uid, bookingId, shopId, barberId, barberName, selectedCuts, customerPhone);
            setMode('done'); setTimeout(onDone, 1800);
        } catch (e) {
            setError(e.code === 'auth/email-already-in-use' ? 'Email already registered. Try signing in.' : e.code === 'auth/wrong-password' ? 'Wrong password.' : e.code === 'auth/user-not-found' ? 'No account found.' : e.code === 'auth/weak-password' ? 'Password must be at least 6 characters.' : 'Something went wrong. Try again.');
        }
        setLoading(false);
    };

    if (mode === 'done') return (
        <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography sx={{ fontSize: 32, mb: 1 }}>🎉</Typography>
            <Typography sx={{ fontFamily: typography.poppins, fontSize: 18, fontWeight: 700, color: colors.textPrimary, mb: 0.5 }}>Account linked!</Typography>
            <Typography sx={{ fontSize: 12, color: colors.textFaint }}>Your visits are now being tracked.</Typography>
        </Box>
    );

    if (mode === 'prompt') return (
        <Box>
            <Box sx={{ bgcolor: colors.bgDeep, border: `1px solid ${colors.border}`, borderRadius: 2, p: 2, mb: 2.5 }}>
                <Typography sx={{ fontSize: 12, color: colors.textMuted, mb: 1.5, fontWeight: 600 }}>With an account you get:</Typography>
                {[['📋','Visit history — "you visited 3 times this month"'],['✂️','Saved preferences — your favourite barber & cut'],['🔁','Rebook instantly next time']].map(([emoji, text]) => (
                    <Box key={text} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <Typography sx={{ fontSize: 14 }}>{emoji}</Typography>
                        <Typography sx={{ fontSize: 12, color: colors.textMuted, lineHeight: 1.4 }}>{text}</Typography>
                    </Box>
                ))}
            </Box>
            <Button fullWidth variant="contained" onClick={handleGoogle} disabled={loading}
                startIcon={loading ? <CircularProgress size={16} sx={{ color: colors.white }} /> : <Google sx={{ fontSize: 18 }} />}
                sx={{ bgcolor: colors.textPrimary, color: colors.bg, fontWeight: 700, fontSize: 13, textTransform: 'none', borderRadius: 2, py: 1.2, mb: 1.5, '&:hover': { bgcolor: colors.textPrimary } }}>
                Continue with Google
            </Button>
            <Button fullWidth onClick={() => setMode('email')}
                sx={{ bgcolor: colors.bgDeep, border: `1px solid ${colors.border}`, color: colors.primary, fontWeight: 600, fontSize: 13, textTransform: 'none', borderRadius: 2, py: 1.2, mb: 1, '&:hover': { bgcolor: colors.bgCard } }}>
                Use email instead
            </Button>
            <Button fullWidth onClick={onDone} sx={{ color: colors.textDisabled, textTransform: 'none', fontSize: 12 }}>No thanks</Button>
        </Box>
    );

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                {['Create account','Sign in'].map((label, i) => (
                    <Box key={label} onClick={() => { setIsNew(i === 0); setError(''); }} sx={{ flex: 1, textAlign: 'center', py: 0.7, borderRadius: 2, cursor: 'pointer', bgcolor: (isNew ? i===0 : i===1) ? colors.primary : colors.bgDeep, border: `1px solid ${(isNew ? i===0 : i===1) ? colors.primary : colors.border}` }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: (isNew ? i===0 : i===1) ? '#322920' : colors.textMuted }}>{label}</Typography>
                    </Box>
                ))}
            </Box>
            <TextField fullWidth size="small" label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} sx={{ ...inputSx, mb: 1.5 }} />
            <TextField fullWidth size="small" label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} sx={{ ...inputSx, mb: error ? 0.5 : 2 }} />
            {error && <Typography sx={{ fontSize: 11, color: colors.error, mb: 1.5 }}>{error}</Typography>}
            <Button fullWidth variant="contained" onClick={handleEmail} disabled={loading}
                sx={{ ...btnPrimary, py: 1.2, mb: 1 }}>
                {loading ? <CircularProgress size={16} sx={{ color: '#322920' }} /> : isNew ? 'Create account' : 'Sign in'}
            </Button>
            <Button fullWidth onClick={() => { setMode('prompt'); setError(''); }} sx={{ color: colors.textFaintest, textTransform: 'none', fontSize: 12 }}>← Back</Button>
        </Box>
    );
};

// ─── Done Popup ───────────────────────────────────────────────────────────────
const DonePopup = ({ bookingId, shopId, barberId, barberName, customerName, customerPhone, shopServices, existingSurcharge, onClose }) => {
    const [selectedCuts,  setSelectedCuts]  = useState([]);
    const [rating,        setRating]        = useState(0);
    const [hoverRating,   setHoverRating]   = useState(0);
    const [review,        setReview]        = useState('');
    const [saving,        setSaving]        = useState(false);
    const [step,          setStep]          = useState('review'); // 'review' | 'total' | 'account' | 'done'
    const [alreadyAuthed, setAlreadyAuthed] = useState(false);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, user => { if (user) setAlreadyAuthed(true); });
        return () => unsub();
    }, []);

    const cutOptions    = (shopServices ?? []).map((s, i) => ({ id: s.id ?? s.name?.toLowerCase().replace(/\s+/g,'_') ?? String(i), label: s.label ?? s.name ?? String(s), price: s.price ?? 0 }));
    const toggleCut     = (cut) => setSelectedCuts(prev => prev.some(c => c.id === cut.id) ? prev.filter(c => c.id !== cut.id) : [...prev, cut]);
    const servicesTotal = selectedCuts.reduce((sum, c) => sum + (c.price ?? 0), 0);
    const surcharge     = existingSurcharge ?? 0;
    const grandTotal    = servicesTotal + surcharge;

    const handleConfirmTotal = async () => {
        setSaving(true);
        try {
            await saveReview({ bookingId, shopId, barberId, barberName, customerName, selectedCuts, rating, review, grandTotal, surcharge, servicesTotal });
        } catch (e) { console.error(e); }
        setSaving(false);
        if (alreadyAuthed) { setStep('done'); setTimeout(onClose, 1800); }
        else setStep('account');
    };

    // ── done ─────────────────────────────────────────────────────────────────
    if (step === 'done') return (
        <Box sx={overlayStyle}>
            <Box sx={cardStyle}>
                <Typography sx={{ fontSize: 48, textAlign: 'center', mb: 1 }}>🙏</Typography>
                <Typography sx={{ fontFamily: typography.poppins, fontSize: 20, fontWeight: 700, color: colors.textPrimary, textAlign: 'center', mb: 0.5 }}>Terima kasih!</Typography>
                <Typography sx={{ fontSize: 13, color: colors.textFaint, textAlign: 'center' }}>See you next time ✂️</Typography>
            </Box>
        </Box>
    );

    return (
        <Box sx={overlayStyle}>
            {/* ── card — uses cardStyle (glassModal) with NO bgcolor override ── */}
            <Box sx={{ ...cardStyle, maxHeight: '90vh', overflowY: 'auto' }}>
                {/* gold accent bar at top */}
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: colors.primary }} />

                {/* ── ACCOUNT ───────────────────────────────────────────────── */}
                {step === 'account' && (
                    <>
                        <Typography sx={{ fontSize: 36, textAlign: 'center', mb: 1 }}>🙏</Typography>
                        <Typography sx={{ fontFamily: typography.poppins, fontSize: 20, fontWeight: 700, color: colors.textPrimary, textAlign: 'center', mb: 0.5 }}>Terima kasih!</Typography>
                        <Typography sx={{ fontSize: 13, color: colors.textFaint, textAlign: 'center', mb: 3 }}>Want to save your visit history?</Typography>
                        <AccountStep bookingId={bookingId} shopId={shopId} barberId={barberId} barberName={barberName} selectedCuts={selectedCuts} customerPhone={customerPhone} onDone={() => { setStep('done'); setTimeout(onClose, 1800); }} />
                    </>
                )}

                {/* ── TOTAL CONFIRMATION ────────────────────────────────────── */}
                {step === 'total' && (
                    <>
                        <Typography sx={{ fontSize: 36, textAlign: 'center', mb: 1 }}>🧾</Typography>
                        <Typography sx={{ fontFamily: typography.poppins, fontSize: 20, fontWeight: 700, color: colors.textPrimary, textAlign: 'center', mb: 0.5 }}>Confirm your bill</Typography>
                        <Typography sx={{ fontSize: 13, color: colors.textFaint, textAlign: 'center', mb: 3 }}>Please confirm the total before we close you out</Typography>

                        {/* Receipt card */}
                        <Box sx={{ bgcolor: colors.bgDeep, border: `1px solid ${colors.border}`, borderRadius: 2.5, overflow: 'hidden', mb: 3 }}>
                            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography sx={{ fontSize: 11, color: colors.textFaintest, textTransform: 'uppercase', letterSpacing: 0.5 }}>Receipt</Typography>
                                <Typography sx={{ fontSize: 11, color: colors.textFaintest }}>{barberName}</Typography>
                            </Box>
                            <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {selectedCuts.length === 0 && surcharge === 0 ? (
                                    <Typography sx={{ fontSize: 13, color: colors.textFaintest, textAlign: 'center', py: 1 }}>No services selected</Typography>
                                ) : (
                                    <>
                                        {selectedCuts.map(c => (
                                            <Box key={c.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography sx={{ fontSize: 13 }}>✂️</Typography>
                                                    <Typography sx={{ fontSize: 13, color: colors.textPrimary }}>{c.label}</Typography>
                                                </Box>
                                                <Typography sx={{ fontSize: 13, color: colors.textMuted, fontWeight: 500 }}>RM {c.price.toFixed(2)}</Typography>
                                            </Box>
                                        ))}
                                        {surcharge > 0 && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography sx={{ fontSize: 13 }}>⭐</Typography>
                                                    <Typography sx={{ fontSize: 13, color: colors.textPrimary }}>Barber selection fee</Typography>
                                                </Box>
                                                <Typography sx={{ fontSize: 13, color: colors.textMuted, fontWeight: 500 }}>RM {surcharge.toFixed(2)}</Typography>
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Box>
                            {/* Total row */}
                            <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${colors.border}`, bgcolor: colors.primaryDim, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>Total</Typography>
                                <Typography sx={{ fontFamily: typography.poppins, fontSize: 26, fontWeight: 900, color: colors.primary, lineHeight: 1 }}>
                                    RM {grandTotal.toFixed(2)}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Rating summary */}
                        {rating > 0 && (
                            <Box sx={{ mb: 3, px: 2, py: 1.2, bgcolor: colors.bgDeep, border: `1px solid ${colors.border}`, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ display: 'flex', gap: 0.3 }}>
                                    {[1,2,3,4,5].map(s => (
                                        <Star key={s} sx={{ fontSize: 16, color: s <= rating ? colors.primary : colors.border }} />
                                    ))}
                                </Box>
                                <Typography sx={{ fontSize: 12, color: colors.textFaint }}>
                                    {['','Poor','Fair','Good','Great','Excellent!'][rating]} · Your rating for {barberName}
                                </Typography>
                            </Box>
                        )}

                        <Button fullWidth variant="contained" onClick={handleConfirmTotal} disabled={saving}
                            sx={{ ...btnPrimary, py: 1.4, fontSize: 15, mb: 1.5 }}>
                            {saving
                                ? <CircularProgress size={18} sx={{ color: '#322920' }} />
                                : grandTotal > 0
                                    ? `✅ Confirm · RM ${grandTotal.toFixed(2)}`
                                    : '✅ Confirm & Close'}
                        </Button>
                        <Button fullWidth onClick={() => setStep('review')}
                            sx={{ color: colors.textFaintest, textTransform: 'none', fontSize: 12 }}>
                            ← Edit services / review
                        </Button>
                    </>
                )}

                {/* ── REVIEW FORM ───────────────────────────────────────────── */}
                {step === 'review' && (
                    <>
                        <Typography sx={{ fontSize: 36, textAlign: 'center', mb: 1 }}>✅</Typography>
                        <Typography sx={{ fontFamily: typography.poppins, fontSize: 20, fontWeight: 700, color: colors.textPrimary, textAlign: 'center', mb: 0.5 }}>Haircut done!</Typography>
                        <Typography sx={{ fontSize: 13, color: colors.textFaint, textAlign: 'center', mb: 3 }}>How was it with {barberName}?</Typography>

                        {/* Services */}
                        {cutOptions.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
                                    <Typography sx={{ fontSize: 12, color: colors.textMuted }}>
                                        What did you get?
                                        <Typography component="span" sx={{ fontSize: 10, color: colors.textFaintest }}> (pick all that apply)</Typography>
                                    </Typography>
                                    {selectedCuts.length > 0 && (
                                        <Typography sx={{ fontSize: 10, color: colors.textFaintest, cursor: 'pointer' }} onClick={() => setSelectedCuts([])}>Clear</Typography>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                                    {cutOptions.map(cut => {
                                        const active = selectedCuts.some(c => c.id === cut.id);
                                        return (
                                            <Box key={cut.id} onClick={() => toggleCut(cut)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, borderRadius: 2, cursor: 'pointer', bgcolor: active ? colors.primaryDim : colors.bgDeep, border: `1px solid ${active ? colors.primary : colors.border}`, transition: 'all .15s' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ width: 18, height: 18, borderRadius: '4px', border: `1.5px solid ${active ? colors.primary : colors.border}`, bgcolor: active ? colors.primary : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                        {active && <CheckCircle sx={{ fontSize: 13, color: '#322920' }} />}
                                                    </Box>
                                                    <Typography sx={{ fontSize: 13, fontWeight: active ? 600 : 400, color: active ? colors.primary : colors.textPrimary }}>{cut.label}</Typography>
                                                </Box>
                                                {cut.price > 0 && (
                                                    <Typography sx={{ fontSize: 12, color: active ? colors.primary : colors.textFaint, fontWeight: active ? 700 : 400 }}>RM{cut.price}</Typography>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>

                                {/* Running total */}
                                {(selectedCuts.length > 0 || surcharge > 0) && (
                                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: colors.bgDeep, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
                                        {selectedCuts.map(c => (
                                            <Box key={c.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                                                <Typography sx={{ fontSize: 11, color: colors.textFaint }}>{c.label}</Typography>
                                                <Typography sx={{ fontSize: 11, color: colors.textMuted }}>RM{c.price}</Typography>
                                            </Box>
                                        ))}
                                        {surcharge > 0 && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                                                <Typography sx={{ fontSize: 11, color: colors.textFaint }}>⭐ Barber selection fee</Typography>
                                                <Typography sx={{ fontSize: 11, color: colors.textMuted }}>RM{surcharge}</Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.8, borderTop: `1px dashed ${colors.border}`, mt: 0.4 }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary }}>Total</Typography>
                                            <Typography sx={{ fontFamily: typography.poppins, fontSize: 16, fontWeight: 700, color: colors.primary }}>RM{grandTotal}</Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Star rating */}
                        <Box sx={{ mb: 2.5 }}>
                            <Typography sx={{ fontSize: 12, color: colors.textMuted, mb: 0.5 }}>
                                Rate your barber
                                <Typography component="span" sx={{ fontSize: 10, color: colors.textFaintest }}> (optional)</Typography>
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: colors.textDisabled, mb: 1 }}>Updates {barberName}'s rating and the shop's overall rating</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {[1,2,3,4,5].map(star => {
                                    const filled = star <= (hoverRating || rating);
                                    return (
                                        <Box key={star} onClick={() => setRating(rating === star ? 0 : star)} onMouseEnter={() => setHoverRating(star)} onMouseLeave={() => setHoverRating(0)} sx={{ cursor: 'pointer', transition: 'transform .1s', '&:hover': { transform: 'scale(1.2)' } }}>
                                            {filled ? <Star sx={{ fontSize: 32, color: colors.primary }} /> : <StarBorder sx={{ fontSize: 32, color: colors.border }} />}
                                        </Box>
                                    );
                                })}
                            </Box>
                            {rating > 0 && <Typography sx={{ fontSize: 11, color: colors.textFaint, mt: 0.5 }}>{['','Poor','Fair','Good','Great','Excellent!'][rating]}</Typography>}
                        </Box>

                        {/* Review text */}
                        <Box sx={{ mb: 3 }}>
                            <Typography sx={{ fontSize: 12, color: colors.textMuted, mb: 1 }}>
                                Leave a comment
                                <Typography component="span" sx={{ fontSize: 10, color: colors.textFaintest }}> (optional)</Typography>
                            </Typography>
                            <TextField fullWidth multiline rows={2} placeholder="Great cut, fast service..." value={review} onChange={e => setReview(e.target.value)} inputProps={{ maxLength: 200 }}
                                sx={{ '& .MuiOutlinedInput-root': { bgcolor: colors.bgDeep, borderRadius: 2, color: colors.textPrimary, fontSize: 13, '& fieldset': { borderColor: colors.border }, '&:hover fieldset': { borderColor: colors.primaryBorder }, '&.Mui-focused fieldset': { borderColor: colors.primary } }, '& textarea': { color: colors.textPrimary }, '& textarea::placeholder': { color: colors.textFaintest, opacity: 1 } }} />
                            <Typography sx={{ fontSize: 10, color: colors.textDisabled, mt: 0.3, textAlign: 'right' }}>{review.length}/200</Typography>
                        </Box>

                        {/* Next — no skip */}
                        <Button fullWidth variant="contained" onClick={() => setStep('total')}
                            sx={{ ...btnPrimary, py: 1.3, fontSize: 14 }}>
                            {grandTotal > 0 ? `Next — Review Bill · RM${grandTotal}` : 'Next — Confirm'}
                        </Button>
                    </>
                )}
            </Box>
        </Box>
    );
};

// ── Overlay + card styles ─────────────────────────────────────────────────────
const overlayStyle = {
    position: 'fixed', inset: 0, zIndex: 999,
    background:           'rgba(0,0,0,0.55)',
    backdropFilter:       'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center', px: 2, pb: 4,
};

const cardStyle = {
    ...glassModal,
    p: 3,
    width: '100%', maxWidth: 400,
    borderRadius: 0,
    position: 'relative', overflow: 'hidden',
    animation: 'slideUp .3s ease',
    '@keyframes slideUp': { from: { transform: 'translateY(40px)', opacity: 0 }, to: { transform: 'translateY(0)', opacity: 1 } },
};

// ─── Main QueueTicket ─────────────────────────────────────────────────────────
const QueueTicket = () => {
    const { shopId, bookingId } = useParams();
    const navigate = useNavigate();

    const [ticket,           setTicket]          = useState(null);
    const [loadingTicket,    setLoadingTicket]    = useState(true);
    const [liveBooking,      setLiveBooking]      = useState(null);
    const [aheadCount,       setAheadCount]       = useState(null);
    const [estimatedMinutes, setEstimatedMinutes] = useState(null);
    const [hasPhone,         setHasPhone]         = useState(false);
    const [customerPhone,    setCustomerPhone]    = useState('');
    const [showDonePopup,    setShowDonePopup]    = useState(false);
    const [popupShown,       setPopupShown]       = useState(false);
    const [shopServices,     setShopServices]     = useState([]);
    const [bookingSurcharge, setBookingSurcharge] = useState(0);
    const [customerName,     setCustomerName]     = useState('');

    useEffect(() => {
        if (!bookingId || !shopId) return;
        const load = async () => {
            try {
                const [bookingSnap, shopSnap] = await Promise.all([getDoc(doc(db,'bookings',bookingId)), getDoc(doc(db,'shops',shopId))]);
                if (!bookingSnap.exists()) { setLoadingTicket(false); return; }
                const data = bookingSnap.data();
                setTicket({ queueNumber: data.queueNumber, bookingId: bookingSnap.id, barberName: data.barberName, barberId: data.barberId, shopName: data.shopName, shopId, slotLabel: data.slotMinutes != null ? formatTime(data.slotMinutes) : '—' });
                setHasPhone(!!data.customerPhone); setCustomerPhone(data.customerPhone ?? '');
                setBookingSurcharge(data.surcharge ?? 0); setCustomerName(data.customerName ?? '');
                if (shopSnap.exists()) setShopServices(shopSnap.data()?.services ?? []);
            } catch (e) { console.error('[QueueTicket] load error:', e); }
            setLoadingTicket(false);
        };
        load();
    }, [bookingId, shopId]);

    useEffect(() => {
        if (!ticket?.barberId) return;
        const today    = new Date(); today.setHours(0,0,0,0);
        const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
        const q = query(collection(db,'bookings'), where('barberId','==',ticket.barberId), where('scheduledAt','>=',Timestamp.fromDate(today)), where('scheduledAt','<',Timestamp.fromDate(tomorrow)));
        const unsub = onSnapshot(q, (snap) => {
            const all  = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            const mine = all.find(b => b.id === ticket.bookingId);
            if (mine) setLiveBooking(mine);
            const active = all.filter(b => b.type === 'walkin' && (b.status === 'waiting' || b.status === 'serving')).sort((a,b) => (a.queueNumber??0) - (b.queueNumber??0));
            const ahead  = active.filter(b => b.queueNumber < ticket.queueNumber && b.id !== ticket.bookingId).length;
            setAheadCount(ahead); setEstimatedMinutes(ahead * 30);
        }, err => console.error('[QueueTicket] listener error:', err));
        return () => unsub();
    }, [ticket]);

    useEffect(() => {
        if (liveBooking?.status === 'completed' && !popupShown) {
            setPopupShown(true); setTimeout(() => setShowDonePopup(true), 800);
        }
    }, [liveBooking?.status]);

    if (loadingTicket) return (
        <Box sx={{ minHeight: '100vh', bgcolor: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: colors.primary }} />
        </Box>
    );

    if (!ticket) return (
        <Box sx={{ minHeight: '100vh', bgcolor: colors.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 3, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 36, mb: 2 }}>🎫</Typography>
            <Typography sx={{ fontSize: 16, color: colors.textFaint }}>Booking not found</Typography>
            <Typography onClick={() => navigate(-1)} sx={{ fontSize: 13, color: colors.primary, mt: 2, cursor: 'pointer' }}>← Go back</Typography>
        </Box>
    );

    const status      = liveBooking?.status;
    const isDone      = status === 'completed';
    const isServing   = status === 'serving';
    const isCancelled = status === 'cancelled';
    const isNext      = aheadCount === 0 && !isServing && !isDone && !isCancelled;

    const statusColor = isDone ? colors.success : isServing ? colors.info : isNext ? colors.primary : colors.textFaint;
    const ringColor   = isDone ? colors.success : isServing ? colors.info : colors.primary;

    return (
        <Box sx={{ minHeight: '100vh', background: colors.bg, backgroundAttachment: 'fixed', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 3, textAlign: 'center', position: 'relative' }}>
            {showDonePopup && (
                <DonePopup
                    bookingId={ticket.bookingId}
                    shopId={shopId}
                    barberId={ticket.barberId}
                    barberName={ticket.barberName}
                    customerName={customerName}
                    customerPhone={customerPhone}
                    shopServices={shopServices}
                    existingSurcharge={bookingSurcharge}
                    onClose={() => setShowDonePopup(false)}
                />
            )}

            <Box sx={{ position: 'absolute', top: 16, left: 16 }}>
                <IconButton onClick={() => navigate(-1)} sx={{ color: colors.textFaintest, backdropFilter: cardBlur, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, '&:hover': { bgcolor: colors.bgDeep } }}>
                    <ArrowBack />
                </IconButton>
            </Box>

            <img src={logo} alt="DigitalQ" style={{ width: 100, height: 'auto', marginBottom: 20 }} />

            <Typography sx={{ fontSize: 48, mb: 1.5 }}>
                {isDone ? '✅' : isServing ? '✂️' : isCancelled ? '❌' : isNext ? '🔔' : '🪑'}
            </Typography>
            <Typography sx={{ fontFamily: typography.poppins, fontSize: 22, fontWeight: 700, color: colors.textPrimary, mb: 0.5 }}>
                {isDone ? 'All done!' : isServing ? "It's your turn!" : isCancelled ? 'Booking cancelled' : isNext ? "You're next!" : "You're in the queue"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: colors.textFaint, mb: 3 }}>{ticket.shopName} · {ticket.barberName}</Typography>

            <Box sx={{ mb: 3, px: 2, py: 0.8, ...glassCard, borderRadius: 2, display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusColor, boxShadow: isServing || isNext ? `0 0 6px ${statusColor}` : 'none' }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: statusColor }}>
                    {isServing ? '✂️ Barber is ready!' : isDone ? 'Visit complete' : isCancelled ? 'Cancelled' : isNext ? '🔔 Make your way up!' : 'Waiting...'}
                </Typography>
            </Box>

            <Box sx={{ width: 140, height: 140, borderRadius: '50%', border: `3px solid ${ringColor}`, boxShadow: `0 0 20px ${ringColor}33`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mb: 3, bgcolor: colors.bgCard, backdropFilter: cardBlur }}>
                <Typography sx={{ fontSize: 11, color: colors.textFaint, mb: 0.3 }}>Your Number</Typography>
                <Typography sx={{ fontFamily: typography.poppins, fontSize: 52, fontWeight: 700, lineHeight: 1, color: ringColor }}>
                    {ticket.queueNumber}
                </Typography>
            </Box>

            {!isDone && !isCancelled && (
                <Box sx={{ ...glassCard, borderRadius: 2, px: 3, py: 2, mb: 3, width: '100%', maxWidth: 320 }}>
                    {aheadCount === null ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <CircularProgress size={14} sx={{ color: colors.primary }} />
                            <Typography sx={{ fontSize: 13, color: colors.textFaint }}>Connecting to queue...</Typography>
                        </Box>
                    ) : isServing ? (
                        <Typography sx={{ fontSize: 15, color: colors.success, fontWeight: 700 }}>🎉 The barber is ready for you!</Typography>
                    ) : isNext ? (
                        <Typography sx={{ fontSize: 14, color: colors.primary, fontWeight: 700 }}>You're next — please make your way up!</Typography>
                    ) : (
                        <Box>
                            <Typography sx={{ fontSize: 12, color: colors.textFaint, mb: 0.5 }}>People ahead of you</Typography>
                            <Typography sx={{ fontFamily: typography.poppins, fontSize: 36, fontWeight: 700, color: colors.primary, lineHeight: 1 }}>{aheadCount}</Typography>
                            {estimatedMinutes > 0 && <Typography sx={{ fontSize: 12, color: colors.textFaintest, mt: 0.5 }}>~{estimatedMinutes} min estimated wait</Typography>}
                        </Box>
                    )}
                </Box>
            )}

            {isDone && !showDonePopup && popupShown && (
                <Button onClick={() => setShowDonePopup(true)} sx={{ mb: 2, color: colors.primary, textTransform: 'none', fontSize: 12, border: `1px solid ${colors.primaryBorder}`, borderRadius: radius.md, px: 2, py: 0.5 }}>
                    🧾 View receipt / review
                </Button>
            )}

            {hasPhone && !isDone && !isCancelled && (
                <Box sx={{ mb: 3, px: 2, py: 0.8, bgcolor: colors.successBg, border: `1px solid ${colors.successBorder}`, borderRadius: 2, display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 14 }}>📱</Typography>
                    <Typography sx={{ fontSize: 12, color: colors.success }}>You'll get a WhatsApp when you're next</Typography>
                </Box>
            )}

            <Box sx={{ ...glassCard, borderRadius: 2, px: 3, py: 2, mb: 2, width: '100%', maxWidth: 320 }}>
                <Typography sx={{ fontSize: 12, color: colors.textFaint, mb: 0.5 }}>Your Time Slot</Typography>
                <Typography sx={{ fontFamily: typography.poppins, fontSize: 28, fontWeight: 700, color: colors.primary }}>{ticket.slotLabel}</Typography>
                <Typography sx={{ fontSize: 12, color: colors.textFaint, mt: 0.5 }}>Today · {ticket.barberName}</Typography>
            </Box>

            <Box sx={{ ...glassCard, borderRadius: 2, px: 2, py: 1.5, width: '100%', maxWidth: 320, mb: 3 }}>
                <Typography sx={{ fontSize: 11, color: colors.textFaintest, mb: 0.3 }}>Booking Reference</Typography>
                <Typography sx={{ fontSize: 11, color: colors.textFaint, fontFamily: 'monospace' }}>{ticket.bookingId}</Typography>
            </Box>

            <Typography sx={{ fontSize: 12, color: colors.textFaintest, maxWidth: 280 }}>
                {isDone ? 'Thanks for visiting! See you next time. ✂️' : 'This page updates automatically. Keep it open.'}
            </Typography>
        </Box>
    );
};

export default QueueTicket;